import { Checkbox, Link, ListItem, Stack, Typography } from '@mui/material';

import { CheckCircleIcon, RadioButtonUncheckedIcon } from '../../../icons';
import { Tag } from '../../atoms/Tag';
import { ResourceMenuActions } from '../../organisms/ResourceMenuActions';
import { type TaskItemProps, useTaskItem } from './useTaskItem';

type Props = TaskItemProps;

export function TaskItem(props: Props) {
  const hook = useTaskItem(props);

  return (
    <ListItem
      disableGutters
      data-testid={props['data-testid']}
      sx={{
        alignItems: 'flex-start',
        py: 0.5,
      }}
    >
      <Checkbox
        checked={hook.isChecked}
        onChange={hook.handleToggle}
        disabled={props.disabled}
        icon={<RadioButtonUncheckedIcon fontSize="small" />}
        checkedIcon={<CheckCircleIcon fontSize="small" />}
        size="small"
        color="success"
        sx={{ p: 0.5, mr: 0.5, mt: -0.25 }}
        slotProps={{
          input: {
            'aria-label': hook.title,
          },
        }}
      />
      <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1, pt: 0.25 }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', flexWrap: 'wrap' }}
        >
          <Typography
            variant="body1"
            sx={{
              textDecoration: hook.isChecked ? 'line-through' : 'none',
              color: hook.isChecked ? 'text.secondary' : 'text.primary',
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {hook.title}
          </Typography>
          {hook.providerName ? (
            <Tag
              label={hook.providerName}
              color="default"
              icon={<hook.ProviderIcon />}
            />
          ) : null}
        </Stack>

        {props.renderSecondary ? (
          props.renderSecondary()
        ) : props.secondary ? (
          typeof props.secondary === 'string' ? (
            <Typography variant="caption">
              {props.secondary}
            </Typography>
          ) : (
            props.secondary
          )
        ) : hook.subTopicName ? (
          props.subTopicHref || props.onSubTopicClick ? (
            <Link
              href={props.subTopicHref}
              onClick={props.onSubTopicClick}
              variant="caption"
              underline="hover"
              sx={{ cursor: 'pointer', display: 'inline-block' }}
            >
              {hook.subTopicName}
            </Link>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {hook.subTopicName}
            </Typography>
          )
        ) : null}
      </Stack>

      {props.action ? (
        <Stack sx={{ flexShrink: 0, ml: 1 }}>{props.action}</Stack>
      ) : hook.hasActions ? (
        <Stack sx={{ flexShrink: 0, ml: 1 }}>
          <ResourceMenuActions
            actions={hook.actions}
            tooltip={hook.actionsButtonTooltip}
            ariaLabel={hook.actionsButtonAriaLabel}
          />
        </Stack>
      ) : null}
    </ListItem>
  );
}

export const OpenTaskItem = TaskItem;
